<script setup lang="ts">
import type { ReportColumn } from '../../models/column.types'
import type { CustomizeMetricGroupKey } from '../../service/customizeGroups'

import CustomizeColumnList from './CustomizeColumnList.vue'

import { useI18n } from '@/shared/i18n'
import { Checkbox, Icon } from '@/shared/ui'

defineProps<{
  groups: readonly { key: CustomizeMetricGroupKey; label: string; columns: ReportColumn[] }[]
  visibleFields: ReadonlySet<string>
  expandedFields: ReadonlySet<string>
  openGroups: ReadonlySet<string>
  expandableFields: readonly string[]
  requiredFields: readonly string[]
}>()

const emit = defineEmits<{
  'group-visible': [columns: readonly ReportColumn[], value: boolean]
  'toggle-group': [key: string]
  visible: [field: string, value: boolean]
  expanded: [field: string, value: boolean]
}>()

const { t } = useI18n()
</script>

<template>
  <div class="customize-metric-groups mt-xs">
    <section v-for="group in groups" :key="group.key" class="customize-metric-groups__group">
      <div class="flex min-h-9 items-center gap-xs">
        <button
          type="button"
          class="flex size-6 flex-none items-center justify-center border-0 bg-transparent p-0 text-primary cursor-pointer"
          :aria-expanded="openGroups.has(`metric:${group.key}`)"
          :aria-label="
            openGroups.has(`metric:${group.key}`)
              ? t('reports.table.collapseGroup', { group: group.label })
              : t('reports.table.expandGroup', { group: group.label })
          "
          @click="emit('toggle-group', `metric:${group.key}`)"
        >
          <Icon name="chevron-right" :size="12" aria-hidden="true" />
        </button>
        <Checkbox
          :model-value="group.columns.every((column) => visibleFields.has(column.field))"
          :aria-label="t('reports.table.selectMetricGroup', { group: group.label })"
          tone="accent"
          @update:model-value="emit('group-visible', group.columns, $event)"
        />
        <button
          type="button"
          class="flex min-w-0 flex-1 items-center justify-between gap-xs border-0 bg-transparent p-0 text-start text-sm font-semibold text-primary cursor-pointer"
          :aria-expanded="openGroups.has(`metric:${group.key}`)"
          @click="emit('toggle-group', `metric:${group.key}`)"
        >
          <span class="truncate">{{ group.label }}</span>
          <Icon
            :name="openGroups.has(`metric:${group.key}`) ? 'chevron-down' : 'chevron-up'"
            :size="12"
            aria-hidden="true"
          />
        </button>
      </div>
      <CustomizeColumnList
        v-if="openGroups.has(`metric:${group.key}`)"
        :columns="group.columns"
        :visible-fields="visibleFields"
        :expanded-fields="expandedFields"
        :expandable-fields="expandableFields"
        :required-fields="requiredFields"
        @visible="(field, value) => emit('visible', field, value)"
        @expanded="(field, value) => emit('expanded', field, value)"
      />
    </section>
  </div>
</template>
